import localizeCurrency from "@helpers/localizeCurrency"
import localizeDate from "@helpers/localizeDate"
import { getPlanRenewalDate } from "@helpers/plans"
import { PaymentIntent } from "@stripe/stripe-js"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Plan } from "src/types"

interface OrderSummaryProps {
  fullPrice: number
  paymentIntent: PaymentIntent | undefined
  plan: Plan
}

export default function OrderSummary({
  fullPrice,
  paymentIntent,
  plan,
}: OrderSummaryProps) {
  const { t } = useTranslation()

  const renewalDate = useMemo(() => getPlanRenewalDate(plan), [plan])

  const localizePrice = (amountInCents: number) =>
    localizeCurrency({ amountInCents, currency: paymentIntent?.currency })

  const discount = paymentIntent ? fullPrice - paymentIntent.amount : 0
  const discountPercent = Math.round((discount / fullPrice) * 100)

  return (
    <div className="vm-card animate-fade-up divide-y divide-vm-line">
      <div className="flex items-center justify-between gap-3 p-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-vm-muted">
            {t("pages.checkout.paywall.payment.yourPlan")}
          </p>
          <p className="mt-0.5 text-base font-bold text-vm-ink">
            {t(`pages.checkout.paywall.planLabels.${plan}`)}
          </p>
        </div>
        <p className="text-right text-xs text-vm-muted">
          {t("pages.checkout.paywall.payment.fullAccess")}
        </p>
      </div>

      {paymentIntent ? (
        <div className="space-y-2 p-4 text-sm">
          {discount > 0 && (
            <>
              <div className="flex justify-between text-vm-muted">
                <span>{t("pages.checkout.paywall.payment.regularPrice")}</span>
                <span className="line-through" translate="no">
                  {localizePrice(fullPrice)}
                </span>
              </div>
              <div className="flex justify-between font-medium text-vm-green-text">
                <span>
                  {t("pages.checkout.paywall.payment.introDiscount", {
                    percent: discountPercent,
                  })}
                </span>
                <span translate="no">-{localizePrice(discount)}</span>
              </div>
            </>
          )}
          <div className="flex items-baseline justify-between pt-1">
            <span className="text-base font-bold text-vm-ink">
              {t("pages.checkout.paywall.payment.totalToday")}
            </span>
            <span
              className="text-2xl font-extrabold text-vm-ink"
              translate="no"
            >
              {localizePrice(paymentIntent.amount)}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 p-4">
          <div className="h-4 w-2/3 animate-pulse rounded bg-vm-line" />
          <div className="h-6 w-full animate-pulse rounded bg-vm-line" />
        </div>
      )}

      <p className="p-4 text-xs leading-relaxed text-vm-muted">
        {t("pages.checkout.paywall.payment.renewal", {
          date: localizeDate(renewalDate),
          price: localizePrice(fullPrice),
        })}
      </p>
    </div>
  )
}
